/**
 * Tickets POS — perfiles de papel + HTML para impresión (Chrome kiosk / window.print).
 *
 * Convención HTML: pageWidth/pageHeight están en CSS px (~72 dpi).
 *
 *  - 58 / 80 mm → térmica: papel = ancho del rollo × alto del contenido
 *  - 100       → oficina A4: papel A4 fijo; canvas = solo el ticket (no la hoja entera)
 *
 * PAPEL_COMUN = 100 (no 0) para evitar valores falsy en forms/JSON.
 */

import QRCode from 'qrcode-generator';

export const PAPEL_COMUN = 100;
export const PAPEL_58 = 58;
export const PAPEL_80 = 80;

/** CSS px por pulgada usado en el layout del ticket. */
const TICKET_CSS_DPI = 72;

/**
 * @typedef {object} TicketProfile
 * @property {number} id
 * @property {string} label
 * @property {string} shortLabel
 * @property {string} hint
 * @property {'thermal'|'office'} kind
 * @property {number} paperMm
 * @property {{ base:number, sm:number, lg:number, xs:number }} font
 * @property {{ t:number, r:number, b:number, l:number }} pad
 * @property {number} lineHeight
 * @property {number} priceColPct
 * @property {number} charsPerLine
 * @property {number} linePx
 * @property {number} ruleGap
 * @property {number} previewWidthPx
 */

/** @type {Record<number, TicketProfile>} */
const PROFILES = {
    [PAPEL_58]: {
        id: PAPEL_58,
        label: 'Ticketera 58 mm',
        shortLabel: '58 mm',
        hint: 'Rollo térmico estrecho. Ideal para kioscos y food trucks.',
        kind: 'thermal',
        paperMm: 58,
        font: { base: 11, sm: 9, lg: 14, xs: 8 },
        pad: { t: 8, r: 6, b: 10, l: 6 },
        lineHeight: 1.3,
        priceColPct: 36,
        charsPerLine: 22,
        linePx: 14,
        ruleGap: 5,
        previewWidthPx: 220,
    },
    [PAPEL_80]: {
        id: PAPEL_80,
        label: 'Ticketera 80 mm',
        shortLabel: '80 mm',
        hint: 'Rollo térmico estándar del mostrador.',
        kind: 'thermal',
        paperMm: 80,
        font: { base: 12, sm: 10, lg: 15, xs: 9 },
        pad: { t: 10, r: 8, b: 12, l: 8 },
        lineHeight: 1.35,
        priceColPct: 32,
        charsPerLine: 32,
        linePx: 16,
        ruleGap: 6,
        previewWidthPx: 300,
    },
    [PAPEL_COMUN]: {
        id: PAPEL_COMUN,
        label: 'Impresora común (A4)',
        shortLabel: 'A4',
        hint: 'Láser o inkjet. Imprime el ticket arriba de una hoja A4.',
        kind: 'office',
        paperMm: 210,
        font: { base: 14, sm: 12, lg: 20, xs: 10 },
        pad: { t: 20, r: 24, b: 20, l: 24 },
        lineHeight: 1.4,
        priceColPct: 28,
        charsPerLine: 48,
        linePx: 20,
        ruleGap: 8,
        previewWidthPx: 420,
    },
};

/** Ancho del ticket en A4 (no el ancho de la hoja). ~110 mm ≈ recibo legible. */
const OFFICE_TICKET_WIDTH_MM = 110;

function mmToIn(mm) {
    return Number(mm) / 25.4;
}

function mmToCssPx(mm) {
    return Math.round(mmToIn(mm) * TICKET_CSS_DPI);
}

function esc(s) {
    return String(s == null ? '' : s).replace(/[<>&]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[ch]));
}

function money(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return '0';
    return x.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function wrappedLines(text, charsPerLine) {
    const t = String(text || '').trim();
    if (!t) return 0;
    return Math.max(1, Math.ceil(t.length / Math.max(8, charsPerLine)));
}

/** Normaliza valor de DB/form. Acepta 0 legacy como impresora común. */
export function parseAnchoMm(value) {
    const n = Number(value);
    if (n === 0 || n === PAPEL_COMUN) return PAPEL_COMUN;
    if (n === PAPEL_58) return PAPEL_58;
    if (n === PAPEL_80) return PAPEL_80;
    return PAPEL_80;
}

export function getTicketProfile(ticketCfgOrMm) {
    const id = typeof ticketCfgOrMm === 'object'
        ? parseAnchoMm(ticketCfgOrMm && ticketCfgOrMm.ancho_mm)
        : parseAnchoMm(ticketCfgOrMm);
    return PROFILES[id] || PROFILES[PAPEL_80];
}

export function listTicketProfiles() {
    return [PROFILES[PAPEL_80], PROFILES[PAPEL_58], PROFILES[PAPEL_COMUN]];
}

export function ticketModoPapel(ticketCfg) {
    return getTicketProfile(ticketCfg).id;
}

export function esPapelComun(ticketCfg) {
    return getTicketProfile(ticketCfg).kind === 'office';
}

export function ticketAnchoMm(ticketCfg) {
    return getTicketProfile(ticketCfg).id;
}

/** Ancho del canvas HTML en CSS px. */
export function ticketPageWidthPx(anchoMm) {
    const profile = getTicketProfile(anchoMm);
    if (profile.kind === 'office') return mmToCssPx(OFFICE_TICKET_WIDTH_MM);
    return mmToCssPx(profile.paperMm);
}

/**
 * Altura del canvas = solo el contenido del ticket (nunca la hoja A4 entera).
 * Evita que el driver pagine de más.
 */
export function estimateTicketHeightPx({ venta, ticketCfg, negocio, arca, comprobante, profile: profileIn }) {
    const c = ticketCfg || {};
    const n = negocio || {};
    const v = venta || {};
    const profile = profileIn || getTicketProfile(c);

    let lines = 0;
    lines += wrappedLines(c.encabezado || n.nombre || 'Ticket', profile.charsPerLine);
    if (c.mostrar_direccion && n.direccion) lines += wrappedLines(n.direccion, profile.charsPerLine);
    if (c.mostrar_telefono && n.telefono) lines += 1;
    if (c.cuit) lines += wrappedLines(c.cuit, profile.charsPerLine);
    lines += 1;
    if (v.ticketFolio) lines += 1;
    if (v.pedidoId) lines += 1;
    if (v.cliente) lines += wrappedLines(`Cliente: ${v.cliente}`, profile.charsPerLine);
    if (v.mesa) lines += wrappedLines(`Mesa: ${v.mesa}`, profile.charsPerLine);
    if (v.direccionEntrega) lines += wrappedLines(`Entrega: ${v.direccionEntrega}`, profile.charsPerLine);
    if (v.nota) lines += wrappedLines(`Nota: ${v.nota}`, profile.charsPerLine);
    if (c.mostrar_vendedor && v.vendedor) lines += 1;
    for (const it of v.items || []) {
        lines += wrappedLines(`${it.q}x ${it.nombre}`, Math.max(8, profile.charsPerLine - 8));
        lines += 1;
    }
    lines += 2;
    if (Number(v.descuentoPct) > 0) lines += 2;
    lines += v.medioPago === 'Efectivo' ? 3 : 1;
    lines += 2 + countTransparenciaFiscalRows({ venta: v, arca, comprobante, ticketCfg: c });
    if (c.leyenda) lines += wrappedLines(c.leyenda, profile.charsPerLine);
    if (c.pie) lines += wrappedLines(c.pie, profile.charsPerLine);

    const qrExtra = profile.id === PAPEL_58 ? 110 : 140;
    const rules = 5;
    const h = lines * profile.linePx
        + rules * profile.ruleGap
        + profile.pad.t
        + profile.pad.b
        + qrExtra
        + 16;

    return Math.max(profile.kind === 'office' ? 240 : 160, h);
}

export function ticketPageHeightPxFromContent(ctx) {
    return estimateTicketHeightPx(ctx);
}

function formatFechaParts(fecha) {
    let d = fecha;
    if (!(d instanceof Date)) {
        const parsed = d ? new Date(d) : null;
        if (parsed && !Number.isNaN(parsed.getTime())) d = parsed;
        else return { fecha: String(fecha || ''), hora: '' };
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return { fecha: `${dd}/${mm}/${yy}`, hora: `${hh}:${mi}` };
}

function unitPriceOf(it) {
    if (!it) return 0;
    if (it.precio != null && it.precio !== '') return Number(it.precio) || 0;
    const q = Number(it.q) || 0;
    const sub = Number(it.subtotal) || 0;
    return q ? sub / q : 0;
}

function buildItemRows(items) {
    return (items || [])
        .map((it) => {
            const pu = unitPriceOf(it);
            return `<tr><td class="desc">${it.q}x ${esc(it.nombre)}<div class="unit">c/u $${money(pu)}</div></td><td class="amt">$${money(it.subtotal)}</td></tr>`;
        })
        .join('');
}

const URL_QR_DENUNCIAS =
    'https://www.argentina.gob.ar/servicio/iniciar-un-reclamo-ante-defensa-del-consumidor';

function roundMoney(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

function isResponsableInscripto(arca) {
    const c = String((arca && arca.condicion_iva) || '').toLowerCase();
    return c === 'responsable_inscripto' || c === 'iva_responsable_inscripto';
}

function numMoney(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function parseAlicuotaPct(raw) {
    if (raw == null || String(raw).trim() === '') return 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(100, n);
}

function ivaContenidoEnPrecio(importe, alicuotaPct) {
    const total = numMoney(importe);
    const ali = parseAlicuotaPct(alicuotaPct);
    if (!(total > 0) || !(ali > 0)) return 0;
    return roundMoney((total * ali) / (100 + ali));
}

function itemSubtotal(it) {
    if (!it) return 0;
    if (it.subtotal != null && it.subtotal !== '') return numMoney(it.subtotal);
    return roundMoney(numMoney(it.q) * numMoney(it.precio));
}

function itemIvaAlicuota(it) {
    if (!it) return null;
    const raw = it.ivaAlicuota != null ? it.ivaAlicuota : it.iva_alicuota;
    if (raw == null || String(raw).trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

function itemImpuestoInterno(it) {
    if (!it) return 0;
    return numMoney(
        it.impuestosInternos != null
            ? it.impuestosInternos
            : it.impuestos_internos != null
              ? it.impuestos_internos
              : it.impInternos != null
                ? it.impInternos
                : it.imp_interno,
    );
}

function alicuotaIvaDefault(arca, venta, comprobante) {
    const cb = comprobante || {};
    const v = venta || {};
    const d = (arca && arca.defaults) || {};
    const n = Number(cb.iva_alicuota || v.ivaAlicuota || v.iva_alicuota || d.iva_alicuota || 21);
    return n > 0 ? n : 21;
}

function ivaDesdeItems(items, total, fallbackAlicuota) {
    const list = Array.isArray(items) ? items : [];
    const itemsSum = roundMoney(list.reduce((s, it) => s + itemSubtotal(it), 0));
    if (!(itemsSum > 0)) return ivaContenidoEnPrecio(total, fallbackAlicuota);
    const scale = total > 0 ? total / itemsSum : 1;
    return roundMoney(
        list.reduce((s, it) => {
            const ali = itemIvaAlicuota(it);
            const rate = ali != null ? ali : fallbackAlicuota;
            return s + ivaContenidoEnPrecio(itemSubtotal(it) * scale, rate);
        }, 0),
    );
}

function calcTransparenciaFiscal({ venta, arca, comprobante, ticketCfg }) {
    const v = venta || {};
    const a = arca || {};
    const cb = comprobante || {};
    const c = ticketCfg || {};
    const items = Array.isArray(v.items) ? v.items : [];
    const total = numMoney(cb.imp_total != null ? cb.imp_total : v.total);
    const ri = isResponsableInscripto(a);
    const defIva = alicuotaIvaDefault(a, v, cb);

    let ivaContenido = 0;
    if (Number(cb.imp_iva) > 0) {
        ivaContenido = Number(cb.imp_iva);
    } else if (Number(v.impIva) > 0) {
        ivaContenido = Number(v.impIva);
    } else if (ri && total > 0) {
        ivaContenido = ivaDesdeItems(items, total, defIva);
    }

    let otrosNac = numMoney(
        v.otrosImpuestosNacionales != null
            ? v.otrosImpuestosNacionales
            : cb.imp_trib != null
              ? cb.imp_trib
              : 0,
    );
    if (!(otrosNac > 0) && items.length) {
        const itemsSum = roundMoney(items.reduce((s, it) => s + itemSubtotal(it), 0));
        const intern = roundMoney(items.reduce((s, it) => s + itemImpuestoInterno(it), 0));
        if (intern > 0) {
            const scale = itemsSum > 0 && total > 0 ? total / itemsSum : 1;
            otrosNac = roundMoney(intern * scale);
        }
    }

    let provinciales = numMoney(v.impuestosProvinciales);
    let municipales = numMoney(v.impuestosMunicipales);
    const aliProv = parseAlicuotaPct(
        v.alicuotaIibb != null ? v.alicuotaIibb : c.alicuota_iibb != null ? c.alicuota_iibb : a.alicuota_iibb,
    );
    const aliMun = parseAlicuotaPct(
        v.alicuotaMunicipal != null
            ? v.alicuotaMunicipal
            : c.alicuota_municipal != null
              ? c.alicuota_municipal
              : a.alicuota_municipal,
    );
    const baseLocal = Math.max(0, total - ivaContenido);
    if (!(provinciales > 0) && aliProv > 0 && baseLocal > 0) {
        provinciales = roundMoney((baseLocal * aliProv) / 100);
    }
    if (!(municipales > 0) && aliMun > 0 && baseLocal > 0) {
        municipales = roundMoney((baseLocal * aliMun) / 100);
    }

    return {
        ivaContenido,
        otrosNac,
        provinciales,
        municipales,
        ivaCorresponde: ri,
    };
}

function countTransparenciaFiscalRows(ctx) {
    const t = calcTransparenciaFiscal(ctx);
    let n = 2;
    if (t.otrosNac > 0) n += 1;
    if (t.provinciales > 0) n += 1;
    if (t.municipales > 0) n += 1;
    return n;
}

function tfRow(desc, amtText) {
    return `<tr><td class="desc">${desc}</td><td class="amt">${amtText}</td></tr>`;
}

function buildTransparenciaFiscalHtml(ctx) {
    const t = calcTransparenciaFiscal(ctx);
    const rows = [];
    const ivaTxt = t.ivaCorresponde
        ? `$${money(t.ivaContenido)}`
        : (t.ivaContenido > 0 ? `$${money(t.ivaContenido)}` : '-');
    rows.push(tfRow('IVA Contenido', ivaTxt));
    if (t.otrosNac > 0) rows.push(tfRow('Otros Imp. Nac. Indirectos', `$${money(t.otrosNac)}`));
    if (t.provinciales > 0) rows.push(tfRow('Impuestos Provinciales', `$${money(t.provinciales)}`));
    if (t.municipales > 0) rows.push(tfRow('Impuestos Municipales', `$${money(t.municipales)}`));
    return `<div class="tf-title">Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)</div>
<table class="sm tf-table">
  ${rows.join('')}
</table>`;
}

function buildDenunciasQrHtml(profile) {
    const qrSize = profile.id === PAPEL_58 ? 96 : 120;
    const cell = profile.id === PAPEL_58 ? 2 : 3;
    const img = qrPngDataUrl(URL_QR_DENUNCIAS, cell);
    return `<img class="denuncias-qr" src="${img}" alt="QR denuncias y reclamos" width="${qrSize}" height="${qrSize}" />
<div class="legal">Denuncias y reclamos</div>
<div class="legal">Defensa del Consumidor</div>`;
}

function buildPaymentRows(venta) {
    if (venta.medioPago === 'Efectivo') {
        return [
            '<tr><td class="desc">Pago</td><td class="amt">Efectivo</td></tr>',
            `<tr><td class="desc">Recibido</td><td class="amt">$${money(venta.recibido)}</td></tr>`,
            `<tr><td class="desc">Vuelto</td><td class="amt">$${money(venta.vuelto)}</td></tr>`,
        ].join('');
    }
    return `<tr><td class="desc">Pago</td><td class="amt">${esc(venta.medioPago || '')}</td></tr>`;
}

function buildTicketStyles(profile, widthPx, heightPx) {
    const p = profile.pad;
    const pricePct = profile.priceColPct;
    const descPct = 100 - pricePct;

    return `
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body {
            width: ${widthPx}px;
            height: ${heightPx}px;
            overflow: hidden;
            background: #fff;
        }
        .ticket {
            width: ${widthPx}px;
            height: ${heightPx}px;
            padding: ${p.t}px ${p.r}px ${p.b}px ${p.l}px;
            font-family: 'Courier New', Courier, monospace;
            color: #000;
            font-size: ${profile.font.base}px;
            line-height: ${profile.lineHeight};
            overflow: hidden;
        }
        .ticket__title {
            text-align: center;
            font-weight: 700;
            font-size: ${profile.font.lg}px;
            line-height: 1.2;
            word-wrap: break-word;
        }
        .ticket__meta {
            text-align: center;
            font-size: ${profile.font.sm}px;
            word-wrap: break-word;
            margin-top: 2px;
        }
        .ticket__info {
            font-size: ${profile.font.sm}px;
            word-wrap: break-word;
            overflow-wrap: anywhere;
            white-space: normal;
        }
        .ticket__info + .ticket__info { margin-top: 2px; }
        .ticket__rule {
            border: 0;
            border-top: 1px dashed #000;
            margin: ${profile.ruleGap}px 0;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
        }
        td {
            vertical-align: top;
            padding: 1px 0;
            word-wrap: break-word;
            overflow-wrap: anywhere;
        }
        td.desc { width: ${descPct}%; padding-right: 6px; }
        td.amt { width: ${pricePct}%; text-align: right; white-space: nowrap; }
        .sm { font-size: ${profile.font.sm}px; }
        .lg { font-size: ${profile.font.lg}px; font-weight: 700; }
        .legal {
            text-align: center;
            font-size: ${profile.font.xs}px;
            word-wrap: break-word;
        }
        .unit {
            font-size: ${profile.font.xs}px;
            opacity: 0.9;
        }
        .tf-title {
            text-align: center;
            font-size: ${profile.font.xs}px;
            font-weight: 700;
            margin: 4px 0 2px;
            word-wrap: break-word;
        }
        .denuncias-qr {
            display: block;
            margin: 8px auto 2px;
            width: ${profile.id === PAPEL_58 ? 96 : 120}px;
            height: auto;
        }
        .foot {
            text-align: center;
            font-size: ${profile.font.sm}px;
            margin-top: 4px;
            word-wrap: break-word;
        }
    `;
}

export function buildTicketHtml({ venta, ticketCfg, negocio, arca, profile: profileIn, widthPx, heightPx }) {
    const c = ticketCfg || {};
    const n = negocio || {};
    const v = venta || {};
    const profile = profileIn || getTicketProfile(c);
    const w = widthPx || ticketPageWidthPx(profile.id);
    const h = heightPx || estimateTicketHeightPx({ venta: v, ticketCfg: c, negocio: n, arca, profile });

    const title = esc(c.encabezado || n.nombre || 'Ticket');
    const meta = [];
    if (c.mostrar_direccion && n.direccion) meta.push(`<div class="ticket__meta">${esc(n.direccion)}</div>`);
    if (c.mostrar_telefono && n.telefono) meta.push(`<div class="ticket__meta">Tel: ${esc(n.telefono)}</div>`);
    if (c.cuit) meta.push(`<div class="ticket__meta">${esc(c.cuit)}</div>`);

    const vendor = c.mostrar_vendedor && v.vendedor
        ? `<div class="ticket__info">Vendedor: ${esc(v.vendedor)}</div>`
        : '';
    const extraInfo = [];
    if (v.pedidoId) extraInfo.push(`<div class="ticket__info">Pedido #${esc(v.pedidoId)}</div>`);
    if (v.cliente) extraInfo.push(`<div class="ticket__info">Cliente: ${esc(v.cliente)}</div>`);
    if (v.mesa) extraInfo.push(`<div class="ticket__info">Mesa: ${esc(v.mesa)}</div>`);
    if (v.direccionEntrega) extraInfo.push(`<div class="ticket__info">Entrega: ${esc(v.direccionEntrega)}</div>`);
    if (v.nota) extraInfo.push(`<div class="ticket__info">Nota: ${esc(v.nota)}</div>`);
    const discountHtml = Number(v.descuentoPct) > 0
        ? `<table class="sm">
  <tr><td class="desc">Subtotal</td><td class="amt">$${money(v.subtotal)}</td></tr>
  <tr><td class="desc">Desc. ${esc(v.descuentoPct)}%</td><td class="amt">-$${money(v.descuentoMonto)}</td></tr>
</table>`
        : '';
    const when = formatFechaParts(v.fecha);
    const tfHtml = buildTransparenciaFiscalHtml({ venta: v, arca, ticketCfg: c });
    const denunciasHtml = buildDenunciasQrHtml(profile);

    return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Ticket</title>
<style>${buildTicketStyles(profile, w, h)}</style></head><body>
<div class="ticket">
  <div class="ticket__title">${title}</div>
  ${meta.join('')}
  <hr class="ticket__rule"/>
  <div class="ticket__info">Fecha: ${esc(when.fecha)}</div>
  <div class="ticket__info">Hora: ${esc(when.hora)}</div>
  ${v.ticketFolio ? `<div class="ticket__info">Comprobante local: ${esc(v.ticketFolio)}</div>` : ''}
  ${extraInfo.join('')}
  ${vendor}
  <hr class="ticket__rule"/>
  <table class="sm">${buildItemRows(v.items)}</table>
  <hr class="ticket__rule"/>
  ${discountHtml}
  <table class="lg"><tr><td class="desc">TOTAL</td><td class="amt">$${money(v.total)}</td></tr></table>
  <table class="sm">${buildPaymentRows(v)}</table>
  <hr class="ticket__rule"/>
  ${tfHtml}
  <hr class="ticket__rule"/>
  ${c.leyenda ? `<div class="legal">${esc(c.leyenda)}</div>` : ''}
  ${c.pie ? `<div class="foot">${esc(c.pie)}</div>` : ''}
  ${denunciasHtml}
</div></body></html>`;
}

export function buildTicketPrintPackage({ venta, ticketCfg, negocio, arca }) {
    const profile = getTicketProfile(ticketCfg);
    const widthPx = ticketPageWidthPx(profile.id);
    const heightPx = estimateTicketHeightPx({ venta, ticketCfg, negocio, arca, profile });
    const html = buildTicketHtml({ venta, ticketCfg, negocio, arca, profile, widthPx, heightPx });

    return {
        html,
        widthPx,
        heightPx,
        profile,
    };
}

/** Venta de ejemplo para preview / prueba de impresión. */
export function buildSampleVenta() {
    return {
        fecha: new Date(),
        vendedor: 'Juan',
        medioPago: 'Efectivo',
        recibido: 5000,
        vuelto: 1700,
        total: 3300,
        items: [
            { q: 2, nombre: 'Gaseosa', precio: 1200, subtotal: 2400 },
            { q: 1, nombre: 'Pan', precio: 900, subtotal: 900 },
        ],
    };
}

/** HTML de preview para el iframe (misma plantilla que imprime). */
export function buildPreviewHtml({ ticketCfg, negocio, negocioNombre, arca }) {
    const n = { ...(negocio || {}) };
    if (!n.nombre) n.nombre = negocioNombre || 'Tu negocio';
    const pkg = buildTicketPrintPackage({
        venta: buildSampleVenta(),
        ticketCfg,
        negocio: n,
        arca,
    });
    return { html: pkg.html, widthPx: pkg.widthPx, heightPx: pkg.heightPx, profile: pkg.profile };
}

export function getTicketPreviewStyle(anchoMm) {
    const profile = getTicketProfile(anchoMm);
    return {
        width: `${profile.previewWidthPx}px`,
        maxWidth: '100%',
    };
}

export function getTicketProfileLabel(anchoMm) {
    return getTicketProfile(anchoMm).label;
}

const COND_IVA_EMISOR_LABEL = Object.freeze({
    monotributo: 'Responsable Monotributo',
    responsable_inscripto: 'IVA Responsable Inscripto',
    exento: 'IVA Sujeto Exento',
    no_responsable: 'Sujeto No Categorizado',
    consumidor_final: 'Consumidor Final',
});

const TIPOS_CBTE_LABEL = Object.freeze({
    1: 'Factura A',
    2: 'Nota de Débito A',
    3: 'Nota de Crédito A',
    6: 'Factura B',
    7: 'Nota de Débito B',
    8: 'Nota de Crédito B',
    11: 'Factura C',
    12: 'Nota de Débito C',
    13: 'Nota de Crédito C',
});

export function labelCondicionIvaEmisor(key) {
    return COND_IVA_EMISOR_LABEL[key] || key || '—';
}

export function labelTipoComprobante(codigo) {
    const n = Number(codigo);
    return TIPOS_CBTE_LABEL[n] || (n ? `Tipo ${n}` : '—');
}

function formatCuitDashed(cuit) {
    const d = String(cuit || '').replace(/\D/g, '');
    if (d.length !== 11) return String(cuit || '');
    return `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}`;
}

function ymdLocal(d = new Date()) {
    const x = d instanceof Date ? d : new Date(d);
    const pad = (n) => String(n).padStart(2, '0');
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}

/**
 * QR oficial de facturas electrónicas (RG ARCA).
 * Texto: https://www.arca.gob.ar/fe/qr/?p={JSON en Base64}
 */
export function buildAfipQrUrl({
    fecha,
    cuit,
    ptoVta,
    tipoCmp,
    nroCmp,
    importe,
    moneda = 'PES',
    ctz = 1,
    tipoDocRec = 99,
    nroDocRec = 0,
    tipoCodAut = 'E',
    cae,
}) {
    const tipoAut = String(tipoCodAut || 'E').toUpperCase() === 'A' ? 'A' : 'E';
    const payload = {
        ver: 1,
        fecha: fecha || ymdLocal(),
        cuit: Number(String(cuit || '').replace(/\D/g, '')),
        ptoVta: Number(ptoVta) || 1,
        tipoCmp: Number(tipoCmp) || 11,
        nroCmp: Number(nroCmp) || 1,
        importe: Number(importe) || 0,
        moneda: String(moneda || 'PES').slice(0, 3).toUpperCase() || 'PES',
        ctz: Number(ctz) || 1,
        tipoDocRec: Number(tipoDocRec) || 99,
        nroDocRec: Number(nroDocRec) || 0,
        tipoCodAut: tipoAut,
        codAut: Number(cae) || 0,
    };
    const json = JSON.stringify(payload);
    let b64;
    if (typeof btoa === 'function') {
        b64 = btoa(unescape(encodeURIComponent(json)));
    } else {
        b64 = Buffer.from(json, 'utf8').toString('base64');
    }
    return `https://www.arca.gob.ar/fe/qr/?p=${b64}`;
}

function qrPngDataUrl(text, cellSize = 3) {
    const qr = QRCode(0, 'M');
    qr.addData(String(text || ''));
    qr.make();
    return qr.createDataURL(cellSize, 0);
}

const DOC_TIPO_LABEL = Object.freeze({
    80: 'CUIT',
    86: 'CUIL',
    96: 'DNI',
    99: 'Consumidor final',
});

function labelDocTipo(codigo) {
    const n = Number(codigo);
    return DOC_TIPO_LABEL[n] || (n ? `Doc ${n}` : 'Consumidor final');
}

function fiscalStyles(profile, w, h) {
    const qrSize = profile.id === PAPEL_58 ? 96 : 120;
    return `${buildTicketStyles(profile, w, h)}
        .fiscal-badge {
            text-align: center;
            font-weight: 700;
            font-size: ${profile.font.lg + 4}px;
            border: 2px solid #000;
            width: 1.6em;
            height: 1.6em;
            line-height: 1.5em;
            margin: 4px auto;
            border-radius: 4px;
        }
        .fiscal-block { text-align: center; font-size: ${profile.font.sm}px; margin-top: 2px; }
        .fiscal-cae { text-align: center; font-size: ${profile.font.xs}px; margin-top: 4px; word-break: break-all; }
        .fiscal-qr { display: block; margin: 8px auto 2px; width: ${qrSize}px; height: auto; }
        .fiscal-note { text-align: center; font-size: ${profile.font.xs}px; margin-top: 4px; color: #444; }
    `;
}

/**
 * Comprobante fiscal para impresión / preview.
 */
export function buildFiscalTicketPrintPackage({
    venta,
    ticketCfg,
    negocio,
    arca,
    comprobante,
    previewNote,
}) {
    const c = ticketCfg || {};
    const n = negocio || {};
    const a = arca || {};
    const cb = comprobante || {};
    const v = venta || {};
    const profile = getTicketProfile(c);
    const w = ticketPageWidthPx(profile.id);

    const tipoCodigo = Number(cb.cbte_tipo)
        || (a.defaults && a.defaults.cbte_tipo)
        || 11;
    const tipoLabel = cb.cbte_tipo_label || labelTipoComprobante(tipoCodigo);
    const letraMatch = String(tipoLabel || '').match(/\b([ABC])\s*$/);
    const letra = (letraMatch && letraMatch[1]) || 'C';
    const pv = Number(cb.punto_venta) || Number(a.punto_venta) || 1;
    const nro = Number(cb.cbte_nro) || 1;
    const total = Number(cb.imp_total != null ? cb.imp_total : v.total) || 0;
    const fechaRaw = cb.cbte_fecha || (v.fecha instanceof Date ? ymdLocal(v.fecha) : ymdLocal());
    const fecha = String(fechaRaw).slice(0, 10);
    const cae = String(cb.cae || '70417054336376');
    const caeVto = String(cb.cae_vto || '').slice(0, 10) || (() => {
        const d = new Date();
        d.setDate(d.getDate() + 10);
        return ymdLocal(d);
    })();

    const docTipo = cb.doc_tipo != null
        ? Number(cb.doc_tipo)
        : Number((a.defaults && a.defaults.doc_tipo) || 99);
    const docNro = cb.doc_nro != null ? Number(cb.doc_nro) : 0;
    const receptor = String(cb.receptor_nombre || v.clienteNombre || 'Consumidor final').trim()
        || 'Consumidor final';

    const qrUrl = buildAfipQrUrl({
        fecha,
        cuit: a.cuit,
        ptoVta: pv,
        tipoCmp: tipoCodigo,
        nroCmp: nro,
        importe: total,
        moneda: cb.moneda || 'PES',
        ctz: cb.ctz != null ? cb.ctz : 1,
        tipoDocRec: docTipo,
        nroDocRec: docNro,
        tipoCodAut: cb.tipo_cod_aut || cb.cae_tipo || 'E',
        cae,
    });
    const qrSize = profile.id === PAPEL_58 ? 96 : 120;
    const qrImg = qrPngDataUrl(qrUrl, profile.id === PAPEL_58 ? 2 : 3);

    const razon = esc(a.razon_social || n.nombre || 'Emisor');
    const cuitFmt = esc(a.cuit_formato || formatCuitDashed(a.cuit) || '—');
    const cond = esc(labelCondicionIvaEmisor(a.condicion_iva));
    const dir = c.mostrar_direccion && n.direccion ? `<div class="ticket__meta">${esc(n.direccion)}</div>` : '';
    const tel = c.mostrar_telefono && n.telefono ? `<div class="ticket__meta">Tel: ${esc(n.telefono)}</div>` : '';

    const items = Array.isArray(v.items) && v.items.length
        ? v.items
        : [
            { q: 2, nombre: 'Gaseosa', precio: 1200, subtotal: 2400 },
            { q: 1, nombre: 'Pan', precio: 900, subtotal: 900 },
        ];
    const itemRows = buildItemRows(items);
    const paymentRows = buildPaymentRows(v);

    const baseH = estimateTicketHeightPx({
        venta: { ...v, items },
        ticketCfg: c,
        negocio: n,
        arca: a,
        comprobante: cb,
        profile,
    });
    const h = baseH + Math.round(profile.linePx * 16) + qrSize + 24;

    const docLine = docTipo === 99 || !docNro
        ? labelDocTipo(docTipo)
        : `${labelDocTipo(docTipo)} ${docNro}`;

    const ivaBlock = Number(cb.imp_iva) > 0
        ? `<table class="sm">
            <tr><td class="desc">Neto</td><td class="amt">$${money(cb.imp_neto)}</td></tr>
            <tr><td class="desc">IVA${cb.iva_alicuota ? ` ${esc(String(cb.iva_alicuota))}%` : ''}</td><td class="amt">$${money(cb.imp_iva)}</td></tr>
          </table>`
        : '';

    const note = previewNote
        ? `<div class="fiscal-note">${esc(previewNote)}</div>`
        : '';

    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Comprobante fiscal</title>
<style>${fiscalStyles(profile, w, h)}</style></head><body>
<div class="ticket">
  <div class="ticket__title">${razon}</div>
  <div class="ticket__meta">CUIT ${cuitFmt}</div>
  <div class="ticket__meta">${cond}</div>
  ${dir}${tel}
  <div class="fiscal-badge">${esc(letra)}</div>
  <div class="fiscal-block"><strong>${esc(tipoLabel)}</strong></div>
  <div class="fiscal-block">PV ${esc(String(pv).padStart(5, '0'))} · N° ${esc(String(nro).padStart(8, '0'))}</div>
  <div class="fiscal-block">Fecha ${esc(fecha)}</div>
  <hr class="ticket__rule"/>
  <div class="ticket__info">${esc(receptor)}</div>
  <div class="ticket__info">Doc: ${esc(docLine)}</div>
  <hr class="ticket__rule"/>
  <table class="sm">${itemRows}</table>
  <hr class="ticket__rule"/>
  ${ivaBlock}
  <table class="lg"><tr><td class="desc">TOTAL</td><td class="amt">$${money(total)}</td></tr></table>
  <table class="sm">${paymentRows}</table>
  <hr class="ticket__rule"/>
  ${buildTransparenciaFiscalHtml({ venta: v, arca: a, comprobante: cb, ticketCfg: c })}
  <hr class="ticket__rule"/>
  <div class="fiscal-cae">CAE ${esc(cae)}</div>
  <div class="fiscal-cae">Vto. CAE ${esc(caeVto)}</div>
  <img class="fiscal-qr" src="${qrImg}" alt="QR ARCA" width="${qrSize}" height="${qrSize}" />
  ${note}
</div></body></html>`;

    return {
        html,
        widthPx: w,
        heightPx: h,
        profile,
        qrUrl,
    };
}

/**
 * Vista previa de comprobante fiscal (muestra) con datos del emisor ARCA + QR oficial.
 */
export function buildFiscalPreviewHtml({ ticketCfg, negocio, arca, qrDataUrl }) {
    void qrDataUrl;
    return buildFiscalTicketPrintPackage({
        ticketCfg,
        negocio,
        arca,
        venta: buildSampleVenta(),
        comprobante: null,
        previewNote: 'Vista previa · datos de ejemplo con QR ARCA',
    });
}

export function buildComandaPrintPackage({ mesaNombre, items, cubiertos, vendedor, ticketCfg }) {
    const profile = getTicketProfile(ticketCfg);
    const widthPx = ticketPageWidthPx(profile.id);
    const rows = (items || []).filter((it) => (Number(it.q) || Number(it.cantidadSeleccionada) || 0) > 0);
    const lineH = profile.linePx + 6;
    const heightPx = Math.max(160, 90 + rows.length * lineH * 2 + (profile.pad.t + profile.pad.b));
    const mesa = esc(mesaNombre || '');
    const vend = vendedor ? esc(vendedor) : '';
    const cub = cubiertos != null && Number(cubiertos) > 0 ? Number(cubiertos) : null;
    const body = rows.map((it) => {
        const q = Number(it.q) || Number(it.cantidadSeleccionada) || 0;
        const nombre = esc(it.nombre || it.producto_nombre || '');
        const nota = String(it.nota || '').trim();
        return `<div class="cmd-line"><span class="cmd-q">${q}×</span><span class="cmd-n">${nombre}</span></div>${
            nota ? `<div class="cmd-nota">↳ ${esc(nota)}</div>` : ''
        }`;
    }).join('');
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Comanda</title>
<style>
html,body{margin:0;padding:0;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:#111}
.cmd{width:${widthPx}px;box-sizing:border-box;padding:${profile.pad.t}px ${profile.pad.r}px ${profile.pad.b}px ${profile.pad.l}px;font-size:${profile.font.base}px;line-height:${profile.lineHeight}}
.cmd h1{margin:0 0 4px;font-size:${profile.font.lg}px;text-align:center;letter-spacing:.12em}
.cmd .meta{text-align:center;font-size:${profile.font.sm}px;margin-bottom:8px}
.cmd hr{border:0;border-top:1px dashed #333;margin:6px 0}
.cmd-line{display:flex;gap:8px;font-weight:700;margin-top:4px}
.cmd-q{flex-shrink:0}
.cmd-n{flex:1}
.cmd-nota{font-size:${profile.font.sm}px;padding-left:1.6em;font-style:italic}
</style></head><body>
<div class="cmd">
  <h1>COMANDA</h1>
  <div class="meta"><strong>Mesa ${mesa}</strong>${cub ? ` · ${cub} cubiertos` : ''}${vend ? `<br>${vend}` : ''}</div>
  <hr/>
  ${body || '<div class="meta">Sin ítems</div>'}
  <hr/>
  <div class="meta">${new Date().toLocaleString('es-AR')}</div>
</div>
</body></html>`;
    return { html, widthPx, heightPx, profile };
}
